import { useCallback, useRef, useState } from 'react';
import { getMergedCachedData, setCachedDocument } from '../Utils/documentCache';

/**
 * useDocumentCache
 *
 * Thin orchestration over the documentCache module so the content container does
 * not own cache lifecycle details.
 *
 * @param {string} documentId  the document being rendered
 * @param {boolean} enabled    caching applies ONLY to normal documents (never
 *                             templates / read-only / conflict-preview / offline)
 *
 * Returns:
 *  - cached:           merged `{ documentData, documentInfo }` read ONCE on mount
 *  - isUiLocked:       true while a cached preview is shown (edits should be locked)
 *  - lockUi/unlockUi:  toggles for the preview lock
 *  - cacheRenderedRef: guards a single instant paint from cache
 *  - commitCache:      writes the latest user-visible state back to the cache
 */
export default function useDocumentCache(documentId, enabled = true) {
  // Read the cache exactly once, lazily, on mount. This is the snapshot we paint.
  const [cached] = useState(() => (enabled ? getMergedCachedData(documentId) : null));

  const [isUiLocked, setIsUiLocked] = useState(false);
  const cacheRenderedRef = useRef(false);

  const lockUi = useCallback(() => setIsUiLocked(true), []);
  const unlockUi = useCallback(() => setIsUiLocked(false), []);

  const commitCache = useCallback(
    (documentData, documentInfo) => {
      if (!enabled || !documentId) return;
      setCachedDocument(documentId, { documentData, documentInfo });
    },
    [enabled, documentId]
  );

  return { cached, isUiLocked, lockUi, unlockUi, cacheRenderedRef, commitCache };
}
